import React, { useEffect, useMemo, useRef, useState } from 'react'

import { useSearchParams } from 'react-router-dom'

import { defaultPaginationURL } from '@hooks/useInfiniteFetch'
import { TitleType } from '@models/Title'
import { usePageApi } from '@providers/PageApiProvider'
import { SearchPageApiResponse } from '@providers/types/api-responses'
import http from '@utils/http'

import { NUM_RESULTS_FOR_PAGE } from '../utils/constants'

const titleSearchUrl = defaultPaginationURL(NUM_RESULTS_FOR_PAGE)

type FetchedTitles = {
  page: number
  titles: TitleType[]
}

type TitlePages = {
  [key: number]: TitleType[]
}

const useInfiniteTitles = (scrollRef: React.RefObject<HTMLDivElement>) => {
  const [search, setSearch] = useSearchParams()
  const { pageData } = usePageApi<SearchPageApiResponse>()

  // Page Info
  const page = Number(search.get('page') ?? 1)

  /**
   * Different than "page" because it retains the old page number to be used on first render. Useful when comparing
   * to see if the page has changed
   */
  const [currentPage, setCurrentPage] = useState(page)
  const lastPage = useMemo(
    () => Math.ceil((pageData?.count ?? 0) / NUM_RESULTS_FOR_PAGE),
    [pageData?.count]
  )
  const [startPage, setStartPage] = useState(page)

  /** Keeps track of the list of titles display */
  const [titlePages, setTitlePages] = useState<TitlePages>({ [page]: pageData?.titles ?? [] })

  /** Stores the pre-loading titles for the previous page (if necessary) */
  const [prevTitles, setPrevTitles] = useState<FetchedTitles | null>(null)

  /** Stores the pre-loading titles for the next page (if necessary) */
  const [nextTitles, setNextTitles] = useState<FetchedTitles | null>(null)

  /** Whether we are fetching the next page of titles */
  const [loadingNext, setLoadingNext] = useState<number | false>(false)

  /** Whether we are fetching the previous page of titles */
  const [loadingPrev, setLoadingPrev] = useState<number | false>(false)

  /**
   * The last scroll position recorded. Used to manage when to check for page loads. We use a ref here as to not
   * rerender every time the value changes
   */
  const lastScroll = useRef(0)

  const preloadNext = async (nextPage: number): Promise<void> => {
    const [resp] = await http.get(titleSearchUrl(nextPage))
    if (resp?.data && loadingNext !== nextPage) {
      setNextTitles({ titles: resp.data.titles, page: nextPage })
    }
  }

  const preloadPrev = async (prevPage: number, show?: boolean): Promise<void> => {
    const [resp] = await http.get(titleSearchUrl(prevPage))
    if (resp?.data && loadingPrev !== prevPage) {
      if (show) {
        showPrev(resp.data.titles, prevPage)
      } else {
        setPrevTitles({ titles: resp.data.titles, page: prevPage })
      }
    }
  }

  const showNext = async (newTitles: TitleType[], nextPage: number): Promise<void> => {
    setTitlePages((prevPages) => ({ ...prevPages, [nextPage]: newTitles }))
    setNextTitles(null)
    if (nextPage < lastPage) {
      await preloadNext(nextPage + 1)
    }
  }

  const showPrev = async (newTitles: TitleType[], prevPage: number): Promise<void> => {
    const prevHeight = scrollRef.current?.scrollHeight ?? null
    const prevScroll = scrollRef.current?.scrollTop ?? null

    setTitlePages((prevPages) => ({ ...prevPages, [prevPage]: newTitles }))
    setPrevTitles(null)
    setStartPage(prevPage)

    setTimeout(() => {
      if (scrollRef.current && prevHeight !== null && prevScroll !== null) {
        const newHeight = scrollRef.current.scrollHeight ?? null
        if (newHeight !== null) {
          scrollRef.current.scrollTop = newHeight - prevHeight + prevScroll
        }
      }
    }, 0)

    if (prevPage > 1) {
      await preloadPrev(prevPage - 1)
    }
  }

  /**
   * Loads the next set of titles. If the titles were preloaded, then those are populated. The next set of titles are
   * then preloaded for when the next page is loaded
   *
   * @param nextPage The next page to load
   */
  const loadNext = async (nextPage: number): Promise<void> => {
    if (nextPage > lastPage || loadingNext) {
      return
    }

    if (nextTitles) {
      // verify preloaded titles are for the correct page
      if (nextTitles.page === nextPage) {
        showNext(nextTitles.titles, nextPage)
        return
      }

      // preloaded titles are invalid, clear
      setNextTitles(null)
    }

    setLoadingNext(nextPage)
    const [resp] = await http.get(defaultPaginationURL(NUM_RESULTS_FOR_PAGE)(nextPage))
    if (resp?.data) {
      showNext(resp.data.titles, nextPage)
    }
    setLoadingNext(false)
  }

  /**
   * Loads the previous set of titles. If the titles were preloaded, then those are populated. The previous set of
   * titles are then preloaded for when the previous page is loaded
   */
  const loadPrev = async (prevPage: number): Promise<void> => {
    if (prevPage < 1 || loadingPrev) {
      return
    }

    if (prevTitles) {
      // verify preloaded titles are for the correct page
      if (prevTitles.page === prevPage) {
        showPrev(prevTitles.titles, prevPage)
        return
      }

      // preloaded titles are invalid, clear
      setPrevTitles(null)
    }

    setLoadingPrev(prevPage)
    const [resp] = await http.get(defaultPaginationURL(NUM_RESULTS_FOR_PAGE)(prevPage))
    if (resp?.data) {
      showPrev(resp.data.titles, prevPage)
    }
    setLoadingPrev(false)
  }

  const scrollToPage = (scrollPage: number) => {
    if (scrollRef.current) {
      const pageElement = scrollRef.current.querySelector(`.item-page[data-page="${scrollPage}"]`)
      if (pageElement) {
        scrollRef.current.scrollTo({
          top: (pageElement as HTMLDivElement).offsetTop - 75,
        })
      }
    }
  }

  // Preload previous or next pages if necessary
  useEffect(() => {
    setTitlePages({})

    if (page > 1) {
      preloadPrev(page - 1, true)
      // Scroll to top of list to hide loading indicator while it loads previous page
      setTimeout(() => {
        scrollToPage(page)
      }, 0)
    }

    if (lastPage > page) {
      preloadNext(page + 1)
    }
  }, [])

  // Reset initial page if the page query gets cleared
  useEffect(() => {
    if (!search.get('page')) {
      setStartPage(1)
    }

    // If the search has changed but the page remains the same, this means our search query was actually updated.
    if (currentPage === page) {
      setPrevTitles(null)
      setNextTitles(null)
    }
    // The search query has changed and it is because the page numbers have changed
    else {
      setCurrentPage(page)
    }
  }, [search])

  // Set initial titles if page data loads
  useEffect(() => {
    setTitlePages((prevPages) => ({ ...prevPages, [page]: pageData?.titles ?? [] }))
    setTimeout(() => {
      scrollToPage(page)
    }, 0)
  }, [pageData?.titles])

  // Initialize scrolling handlers
  useEffect(() => {
    const scrollNode = scrollRef.current
    if (!scrollNode) return

    const onScroll = async (): Promise<void> => {
      const scrollPos = scrollNode.scrollTop
      const pageElements = scrollNode.getElementsByClassName('item-page')

      if (Math.abs(scrollPos - lastScroll.current) > scrollNode.offsetHeight * 0.1) {
        lastScroll.current = scrollPos

        // Bottom of scroll container
        const scrollCenter = scrollNode.scrollTop + scrollNode.clientHeight

        // Go through page elements in reverse
        for (let i = pageElements.length - 1; i >= 0; i--) {
          const el = pageElements[i] as HTMLDivElement

          if (el.offsetTop < scrollCenter) {
            const dataPage = el.getAttribute('data-page')
            if (dataPage && Number(dataPage) !== page) {
              search.set('page', dataPage)
              setSearch(search, { replace: true, state: { shallow: true } })
            }
            break // always break on the first page element visible
          }
        }
      }

      if (scrollPos >= 0.9 * (scrollNode.scrollHeight - scrollNode.clientHeight)) {
        if (!loadingNext) {
          let nextPage = page + 1
          if (pageElements.length > 0) {
            nextPage = Number(pageElements[pageElements.length - 1].getAttribute('data-page')) + 1
          }

          await loadNext(nextPage)
        }
      }

      let bottomThreshold = 0.1 * scrollNode.clientHeight
      if (pageElements.length > 0) bottomThreshold += pageElements[0].clientHeight
      if (scrollPos <= bottomThreshold) {
        if (!loadingPrev) {
          let prevPage = page - 1
          if (pageElements.length > 0) {
            prevPage = Number(pageElements[0].getAttribute('data-page')) - 1
          }
          await loadPrev(prevPage)
        }
      }
    }

    scrollNode.addEventListener('scroll', onScroll)

    return () => {
      scrollNode.removeEventListener('scroll', onScroll)
    }
  }, [scrollRef, search, loadingNext, loadingPrev, page, setSearch, loadNext, loadPrev])

  const titles = useMappedTitles(titlePages)
  return { startPage, titles, page, lastPage }
}

export default useInfiniteTitles

const useMappedTitles = (titlePages: TitlePages): TitleType[] => {
  return useMemo(
    () =>
      Object.keys(titlePages)
        .map((page) => Number(page))
        .sort()
        .flatMap((page) => titlePages[page]),
    [titlePages]
  )
}
